'use client';

/**
 * SystemObeyaExplorer - Standard Reports browser
 *
 * Lists available Obeya analytical views. Click to load
 * the report in the canvas panel.
 *
 * @layer presentation
 * @arch_component_type explorer
 * @arch_domain system_obeya
 */

import { useState } from 'react';
import {
  Target,
  Table2,
  ShieldAlert,
  TrendingUp,
  Bug,
  History,
  LayoutDashboard,
  BrainCircuit,
  DollarSign,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils/cn';
import { useGraphSelectionStore } from '@/lib/stores/graph-selection-store';

// ---------------------------------------------------------------------------
// Report definitions
// ---------------------------------------------------------------------------

interface ReportDef {
  id: string;
  name: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
}

const REPORTS: ReportDef[] = [
  { id: 'summary', name: 'Summary', description: 'High-level counts: packages, capabilities, findings, coverage', icon: LayoutDashboard },
  { id: 'module_summary', name: 'Module Summary', description: 'Complete package overview with productivity metrics', icon: Table2 },
  { id: 'gap_analysis', name: 'Gap Analysis', description: 'Packages with/without test coverage', icon: ShieldAlert },
  { id: 'value_map', name: 'Value Map', description: 'Packages ranked by function points and quality', icon: TrendingUp },
  { id: 'debt_density', name: 'Debt Density', description: 'Findings per function point — hotspot detection', icon: Bug },
  { id: 'high_cc_functions', name: 'Complexity Hotspots', description: 'High cyclomatic complexity functions by module', icon: BrainCircuit },
  { id: 'cost_breakdown', name: 'Cost Breakdown', description: 'Per-module and per-stage analysis economics', icon: DollarSign },
  { id: 'history', name: 'Run History', description: 'Analysis run evolution over time', icon: History },
];

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function SystemObeyaExplorer() {
  const selectNode = useGraphSelectionStore((s) => s.selectNode);
  const selectedNode = useGraphSelectionStore((s) => s.selectedNode);

  const handleSelectReport = (report: ReportDef) => {
    selectNode({
      id: report.id,
      type: report.id,  // Matrix key — e.g. "module_summary", "high_cc_functions"
      label: report.name,
      properties: {
        description: report.description,
      },
    });
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="p-3 border-b border-border">
        <div className="flex items-center gap-2">
          <Target className="w-4 h-4 text-muted-foreground" />
          <span className="text-sm font-medium">Standard Reports</span>
          <Badge variant="secondary" className="text-[10px] px-1.5 ml-auto">
            {REPORTS.length}
          </Badge>
        </div>
      </div>

      {/* Report List */}
      <div className="flex-1 overflow-auto py-1">
        {REPORTS.map((report) => {
          const Icon = report.icon;
          const isSelected = selectedNode?.id === report.id;

          return (
            <div
              key={report.id}
              className={cn(
                'flex items-start gap-3 px-3 py-2.5 cursor-pointer',
                'hover:bg-accent/50 transition-colors',
                isSelected && 'bg-accent'
              )}
              onClick={() => handleSelectReport(report)}
            >
              <Icon className="w-4 h-4 mt-0.5 shrink-0 text-muted-foreground" />
              <div className="min-w-0">
                <div className="text-sm font-medium">{report.name}</div>
                <div className="text-xs text-muted-foreground mt-0.5">
                  {report.description}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
